import { Anfrage } from "@/model/Anfrage";
import Logger from "js-logger";
import keycloakStore from "@/businesslogic/stores/keycloakStore";
import axios from "axios";
import { ZusaetzlicheDaten } from "@/businesslogic/tracking/zusaetzlicheDaten";
import tracking from "@/businesslogic/tracking/tracking";
import { ref } from "vue";
import anfrageBereinigung from "@/businesslogic/utils/anfrageBereinigung";

class FerienhausAnfrageService {
  http() {
    const keycloakAccessToken = keycloakStore().getKeycloakAccessToken();

    let headers;
    if (keycloakAccessToken) {
      headers = {
        Authorization: "Bearer " + keycloakAccessToken,
        "Content-Type": "application/json",
      };
    } else {
      headers = {
        "Content-Type": "application/json",
      };
    }

    return axios.create({
      baseURL: process.env.VUE_APP_BACKEND_URL,
      headers: headers,
    });
  }

  getBaseUrl(isMakler: boolean, isMaklerLink: boolean) {
    if (!isMakler) {
      return "/api/direkt/";
    }
    return isMaklerLink ? "/api/makler-tarifrechner/" : "/api/makler/";
  }

  private static postProcess(anfrage: Anfrage): Anfrage {
    const anfrageKopie: Anfrage = JSON.parse(JSON.stringify(anfrage));
    anfrageKopie.zahlungsdaten.iban = anfrageKopie.zahlungsdaten.iban.replace(/ /g, "");
    // Bereinigt Anfrage vor dem Absenden ans Backend (u.a. TARGET-4788)
    anfrageBereinigung(anfrageKopie);
    return anfrageKopie;
  }

  private static track(
    sessionToken: string,
    kontext: string,
    ereignis: string,
    anfrage?: Anfrage,
    zusaetzlicheDaten?: ZusaetzlicheDaten,
  ) {
    const { track } = tracking();
    track(sessionToken, kontext, ereignis, anfrage, zusaetzlicheDaten);
  }

  herunterladenFHK(
    sessionToken: string,
    anfrage: Anfrage,
    isAntrag: boolean,
    kontext: string,
    trackingZusatz: string | undefined,
    produktId: string,
  ) {
    Logger.debug("herunterladen(): Dokument herunterladen...", anfrage);
    if (trackingZusatz) {
      trackingZusatz = "_" + trackingZusatz;
    }

    return this.http()
      .post(
        this.getBaseUrl(anfrage.isMakler, anfrage.isMaklerLink) +
          "vorschlag/" +
          (isAntrag ? "a" : "v") +
          "/herunterladen/" +
          produktId +
          "?vorgangAnlegen=" +
          (kontext === "Abschluss" ? "false" : "true"),
        FerienhausAnfrageService.postProcess(anfrage),
        {
          headers: {
            S_Token: sessionToken,
          },
        },
      )
      .then((value) => {
        anfrage.anfrageNummer = value.data.anfrageNummer;

        FerienhausAnfrageService.track(sessionToken, kontext, "buendel_herunterladen" + trackingZusatz, anfrage);
        return value;
      });
  }

  versenden(sessionToken: string, anfrage: Anfrage, isAntrag: boolean, kontext: string, produktId: string) {
    Logger.debug("versenden(): Sende Daten...", anfrage);

    return this.http()
      .post(
        this.getBaseUrl(anfrage.isMakler, anfrage.isMaklerLink) +
          "vorschlag/" +
          (isAntrag ? "a" : "v") +
          "/versenden/" +
          produktId,
        FerienhausAnfrageService.postProcess(anfrage),
        {
          headers: {
            S_Token: sessionToken,
          },
        },
      )
      .then((value) => {
        anfrage.anfrageNummer = value.data.anfrageNummer;

        FerienhausAnfrageService.track(sessionToken, kontext, "buendel_email_versenden", anfrage);
        return value;
      });
  }

  speichern(sessionToken: string, anfrage: Anfrage, isAntrag: boolean, kontext: string, produktId: string) {
    Logger.debug("zwischenspeichern(): Sende Daten...", anfrage);
    return this.http()
      .post(
        this.getBaseUrl(anfrage.isMakler, anfrage.isMaklerLink) +
          "vorschlag/" +
          (isAntrag ? "a" : "v") +
          "/speichern/" +
          produktId,
        FerienhausAnfrageService.postProcess(anfrage),
        {
          headers: {
            S_Token: sessionToken,
          },
        },
      )
      .then((value) => {
        anfrage.anfrageNummer = value.data.anfrageNummer;

        FerienhausAnfrageService.track(sessionToken, kontext, "buendel_zwischenspeichern", anfrage);
        return value;
      });
  }

  produktNameAufloesen(produktId: string) {
    if (produktId === "HW63000G0000") {
      return "Wohngebäudeversicherung";
    }
    if (produktId === "TFX2000G0000") {
      return "Hausratversicherung";
    }
    if (produktId === "6S23000G0000") {
      return "Haus-_und_Grundbesitzerhaftplicht";
    }
  }

  getFHKProdukte(storeData: Anfrage) {
    const produkte = ref(new Array<string>());
    const versicherungsumfang = storeData.ferienhaus.versicherungsumfang;
    if (versicherungsumfang === "0001" || versicherungsumfang === "0002") {
      produkte.value.push("HW63000G0000");
    }
    if (versicherungsumfang === "0001" || versicherungsumfang === "0003") {
      produkte.value.push("TFX2000G0000");
    }
    if (storeData.tarifinformationen.hausundgrundbesitzerhaftpflicht === true) {
      produkte.value.push("6S23000G0000");
    }
    return produkte.value;
  }
}

export default new FerienhausAnfrageService();
